<!--
  @component Sparkline — tiny inline SVG line/area chart.
  tier:      primitive (visual atom)
  purpose:   show a metric's recent shape (CPU, RPS, bandwidth…) inline
             on Overview + Performance. Client-accumulated point series;
             a fuller Chart can build on the same path math later.
-->
<script lang="ts">
  interface Props {
    values: number[];
    width?: number;
    height?: number;
    /** Stroke color (CSS). Defaults to a neutral foreground tint. */
    color?: string;
    /** Fill a soft area under the line. */
    area?: boolean;
    strokeWidth?: number;
  }
  let {
    values,
    width = 120,
    height = 32,
    color = "var(--text-secondary)",
    area = true,
    strokeWidth = 1.5,
  }: Props = $props();

  const PAD = 2;

  const geom = $derived.by(() => {
    const n = values.length;
    if (n === 0) return { line: "", fill: "" };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = n > 1 ? (width - PAD * 2) / (n - 1) : 0;
    const pts = values.map((v, i) => {
      const x = PAD + i * stepX;
      const y = PAD + (1 - (v - min) / span) * (height - PAD * 2);
      return [x, y] as const;
    });
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const last = pts[pts.length - 1]![0];
    const first = pts[0]![0];
    const fill = `${line} L${last.toFixed(1)},${height - PAD} L${first.toFixed(1)},${height - PAD} Z`;
    return { line, fill };
  });

  const gradId = `sl-${Math.random().toString(36).slice(2, 8)}`;
</script>

<svg
  class="sparkline"
  viewBox="0 0 {width} {height}"
  width={width}
  height={height}
  preserveAspectRatio="none"
  aria-hidden="true"
>
  {#if area && geom.fill}
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color={color} stop-opacity="0.18" />
        <stop offset="100%" stop-color={color} stop-opacity="0" />
      </linearGradient>
    </defs>
    <path d={geom.fill} fill="url(#{gradId})" stroke="none" />
  {/if}
  {#if geom.line}
    <path d={geom.line} fill="none" stroke={color} stroke-width={strokeWidth} stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
  {/if}
</svg>

<style>
  .sparkline { display: block; overflow: visible; }
</style>
